import React, { useState, useEffect } from "react";
import { motion, AnimatePresence } from "framer-motion";
import { X, FileText, ExternalLink } from "lucide-react";
import API from "../../utils/api";

const DisclosureModal = ({ isOpen, onClose }) => {
  const [disclosures, setDisclosures] = useState({
    set: false,
    data: {},
    url: "",
    loaded: false,
  });
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (isOpen) {
      fetchDisclosures();
    }
  }, [isOpen]);

  const fetchDisclosures = async () => {
    setLoading(true);
    try {
      const response = await API.get("/disclosures");
      if (!response.data.result_code) {
        if (response.data.response) {
          response.data.response.sort((a, b) => {
            if (a.Data.NoticesResponse.order < b.Data.NoticesResponse.order)
              return -1;
            if (a.Data.NoticesResponse.order > b.Data.NoticesResponse.order)
              return 1;
            return 0;
          });
          setDisclosures({ set: true, data: response.data, loaded: true });
        } else if (response.data.external_disclosure_url) {
          setDisclosures({
            set: true,
            data: {},
            url: response.data.external_disclosure_url,
          });
        }
      }
    } catch (e) {
      console.error("Error fetching disclosures:", e);
    } finally {
      setLoading(false);
    }
  };

  if (!isOpen) return null;

  return (
    <AnimatePresence>
      {isOpen && (
        <>
          {/* Backdrop */}
          <motion.div
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={onClose}
            className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50"
          />

          {/* Modal */}
          <motion.div
            initial={{ opacity: 0, scale: 0.95 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.95 }}
            className="fixed inset-x-4 top-[50%] translate-y-[-50%] max-w-4xl mx-auto bg-[#0a1929] rounded-3xl shadow-2xl z-50 max-h-[80vh] overflow-hidden border border-white/10"
          >
            {/* Header */}
            <div className="flex items-center justify-between p-6 border-b border-white/10">
              <div className="flex items-center gap-3">
                <div className="p-2 rounded-xl bg-blue-500/20">
                  <FileText className="w-5 h-5 text-blue-400" />
                </div>
                <h2 className="text-xl font-semibold text-white">
                  Consumer Rights & Disclosures
                </h2>
              </div>
              <button
                onClick={onClose}
                className="p-2 rounded-xl hover:bg-white/10 transition-colors"
              >
                <X className="w-5 h-5 text-white/70" />
              </button>
            </div>

            {/* Content */}
            <div className="p-6 overflow-y-auto max-h-[calc(80vh-140px)]">
              {loading ? (
                <div className="flex items-center justify-center py-12">
                  <div className="text-white/60">Loading disclosures...</div>
                </div>
              ) : disclosures.url ? (
                <div className="h-[60vh]">
                  <iframe
                    src={disclosures.url}
                    className="w-full h-full rounded-xl border border-white/10"
                    title="Disclosures"
                    sandbox="allow-same-origin allow-scripts allow-forms"
                  />
                </div>
              ) : disclosures.data?.response ? (
                <div className="space-y-6">
                  {disclosures.data.response.map((item, index) => (
                    <div key={index} className="space-y-3">
                      <h3 className="text-lg font-semibold text-white">
                        {item.Data.NoticesResponse.title}
                      </h3>
                      <div
                        className="text-white/70 prose prose-invert max-w-none"
                        dangerouslySetInnerHTML={{
                          __html: item.Data.NoticesResponse.html,
                        }}
                      />
                    </div>
                  ))}
                </div>
              ) : (
                <div className="text-white/60">
                  No disclosures available at this time.
                </div>
              )}
            </div>

            {/* Footer */}
            <div className="p-6 border-t border-white/10">
              <button
                onClick={onClose}
                className="w-full px-6 py-3 rounded-2xl bg-white/10 text-white hover:bg-white/20 transition-colors font-medium"
              >
                Close
              </button>
            </div>
          </motion.div>
        </>
      )}
    </AnimatePresence>
  );
};

export default DisclosureModal;